import { makeAutoObservable, observable, runInAction } from "mobx";
import { assertUploadSize, logUploadFailure, uploadLimitFor } from "@/lib/upload-limits";
import { peekQueryClient } from "@/queries/client";
import { templateAttachmentsKey } from "@/queries/template-attachments";
import type { RootStore } from "./root.store";
import { queryCacheScope } from "./query-cache-scope";
import type { TWorkitemTemplateAttachment } from "@/types/project-template";

/**
 * The writers for attachments on template work items (upload, delete) and the
 * pending state the panel shows: uploads keyed by the template work item id,
 * deletes by the attachment id. The list
 * itself is a query (`app/queries/template-attachments.ts`), read through
 * `useTemplateAttachments`; a write edits the cached list. Only saved work
 * items (a real server id, not a ``draft-…`` client key) can carry
 * attachments — the panel gates uploads until the template is saved.
 */
export class WorkitemTemplateAttachmentsStore {
  uploadingByWorkitemId = observable.map<string, boolean>();
  /** Why the last upload failed. The error itself, held by reference rather
   *  than made observable: the panel turns it into words (NG-3279). */
  uploadFailureByWorkitemId = observable.map<string, unknown>(undefined, { deep: false });
  /** How much of the file in flight has reached storage, from 0 to 1 (NG-3279). */
  uploadProgressByWorkitemId = observable.map<string, number>();
  deletingAttachmentIds = observable.set<string>();
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /**
   * Apply a write's edit to the cached list (`app/queries/template-attachments.ts`)
   * that the panel reads, so it shows without a re-read. Does nothing before
   * it was read.
   */
  private patchCachedAttachments(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    edit: (current: TWorkitemTemplateAttachment[]) => TWorkitemTemplateAttachment[],
  ): void {
    const scope = queryCacheScope(workspaceSlug, "WorkitemTemplateAttachmentsStore");
    const client = scope ? peekQueryClient() : null;
    if (!scope || !client) return;
    client.setQueryData<TWorkitemTemplateAttachment[]>(
      templateAttachmentsKey(scope, templateId, workitemId),
      (current) => (current ? edit(current) : current),
    );
  }

  isUploading(workitemId: string): boolean {
    return this.uploadingByWorkitemId.get(workitemId) ?? false;
  }

  /** A delete of this attachment is in flight; its row stays until it lands. */
  isDeleting(attachmentId: string): boolean {
    return this.deletingAttachmentIds.has(attachmentId);
  }

  /** How much of this work item's upload has been sent, or null before its bytes move. */
  getUploadProgress(workitemId: string): number | null {
    return this.uploadProgressByWorkitemId.get(workitemId) ?? null;
  }

  /** Why the last upload to this work item failed, or undefined when it did not. */
  getUploadFailure(workitemId: string): unknown {
    return this.uploadFailureByWorkitemId.get(workitemId);
  }

  async uploadAttachment(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    file: File,
  ): Promise<void> {
    this.uploadingByWorkitemId.set(workitemId, true);
    this.uploadFailureByWorkitemId.delete(workitemId);
    try {
      // Refused before the presign when over the limit, so nothing is sent.
      assertUploadSize(file, uploadLimitFor(this.rootStore.instance.config, "file"));
      const attachment =
        await this.rootStore.projectTemplateService.uploadWorkitemTemplateAttachment(
          workspaceSlug,
          templateId,
          workitemId,
          file,
          {
            // Whole percents: no re-render for a change nobody could see.
            onProgress: (fraction) =>
              runInAction(() => {
                this.uploadProgressByWorkitemId.set(workitemId, Math.floor(fraction * 100) / 100);
              }),
          },
        );
      runInAction(() => {
        this.uploadingByWorkitemId.set(workitemId, false);
        this.uploadProgressByWorkitemId.delete(workitemId);
      });
      // A concurrent query refetch can land between the upload resolving and
      // this patch running, and by then the server already has the new row —
      // so `current` may already carry it under the same server-assigned id.
      // Blindly prepending double-rendered the row under the exact same name
      // (a Playwright strict-mode violation, #e2e flake on
      // project-template-work-item-attachments.spec.ts). Dedup by id instead.
      this.patchCachedAttachments(workspaceSlug, templateId, workitemId, (current) =>
        current.some((a) => a.id === attachment.id) ? current : [attachment, ...current],
      );
    } catch (error) {
      logUploadFailure(error, "a template work-item attachment", {
        workspaceSlug,
        templateId,
        workitemId,
        size: file.size,
      });
      runInAction(() => {
        this.uploadingByWorkitemId.set(workitemId, false);
        this.uploadProgressByWorkitemId.delete(workitemId);
        this.uploadFailureByWorkitemId.set(workitemId, error);
      });
    }
  }

  async deleteAttachment(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    attachmentId: string,
  ): Promise<void> {
    // The row leaves the list only once the server confirms, so a vanished
    // row means the delete landed, for the user and for a spec that reloads.
    this.deletingAttachmentIds.add(attachmentId);
    try {
      await this.rootStore.projectTemplateService.deleteWorkitemTemplateAttachment(
        workspaceSlug,
        templateId,
        workitemId,
        attachmentId,
      );
      this.patchCachedAttachments(workspaceSlug, templateId, workitemId, (current) =>
        current.filter((a) => a.id !== attachmentId),
      );
    } catch (error) {
      console.error("Failed to delete template work-item attachment", {
        workspaceSlug,
        templateId,
        workitemId,
        attachmentId,
        error,
      });
    } finally {
      runInAction(() => {
        this.deletingAttachmentIds.delete(attachmentId);
      });
    }
  }
}
